import currency from "currency.js";

const money = value =>
  `${String.fromCharCode(8358)}${Number(value || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
const escapeHtml = value =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

export const printReceipt = ({
  transactionId,
  cart,
  paymentMethod,
  amountPaid,
  cashierName,
  splitPayments = []
}) => {
  const items = cart && cart.items
    ? Object.keys(cart.items).map(key => cart.items[key])
    : [];

  const summary = (cart && cart.summary) || {};

  const subtotal = Number(summary.total || 0);
  const itemDiscount = Number(summary.discountOnItems || 0);
  const totalDiscount = currency(summary.discountOnItems || 0)
    .add(summary.discountOnTotal || 0)
    .value;
  const tax = Number(summary.taxAmount || 0);
  const total = Number(summary.netTotal || 0);
  const paid = Number(amountPaid || 0);
  const change =
    paymentMethod === "CASH"
      ? Math.max(0, paid - total)
      : Math.max(0, paid - total);

  const now = new Date();

  const dateString = now.toLocaleDateString("en-NG", {
    year: "numeric",
    month: "short",
    day: "2-digit"
  });

  const timeString = now.toLocaleTimeString("en-NG", {
    hour: "2-digit",
    minute: "2-digit"
  });

  const paymentRows =
    paymentMethod === "SPLIT" && splitPayments.length > 0
      ? splitPayments
          .map(
            payment => `
              <div class="row">
                <span>${escapeHtml(payment.paymentMethod)}</span>
                <span>${money(payment.amount)}</span>
              </div>
            `
          )
          .join("")
      : `
          <div class="row">
            <span>${escapeHtml(paymentMethod)}</span>
            <span>${money(paid)}</span>
          </div>
        `;

  const itemRows = items
    .map(
      item => `
        <div class="item">
          <div class="item-name">${escapeHtml(item.name)}</div>

          <div class="item-detail">
            <span>
              ${Number(item.qty || 0)} &times; ${money(item.sellingPrice)}
            </span>

            <span>
              ${money(item.totalPrice)}
            </span>
          </div>
        </div>
      `
    )
    .join("");

  const receiptHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />
        <title>Receipt #${escapeHtml(transactionId)}</title>

        <style>
          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            font-family: Arial, Helvetica, sans-serif;
            color: #111111;
          }

          body {
            width: 80mm;
            margin: 0 auto;
            padding: 8px;
            font-size: 12px;
          }

          .receipt {
            width: 100%;
          }

          .center {
            text-align: center;
          }

          .title {
            font-size: 20px;
            font-weight: 900;
            letter-spacing: 1px;
            margin-bottom: 3px;
          }

          .subtitle {
            font-size: 11px;
            color: #555555;
          }

          .meta {
            margin-top: 12px;
            padding-bottom: 10px;
            border-bottom: 1px dashed #777777;
          }

          .meta-row,
          .row {
            display: flex;
            justify-content: space-between;
            gap: 10px;
            line-height: 19px;
          }

          .items {
            padding: 10px 0;
            border-bottom: 1px dashed #777777;
          }

          .item {
            margin-bottom: 9px;
          }

          .item-name {
            font-weight: 700;
            line-height: 16px;
          }

          .item-detail {
            display: flex;
            justify-content: space-between;
            gap: 8px;
            color: #333333;
            margin-top: 2px;
          }

          .summary {
            padding: 10px 0;
            border-bottom: 1px dashed #777777;
          }

          .grand-total {
            font-size: 16px;
            font-weight: 900;
            margin-top: 5px;
            padding-top: 7px;
            border-top: 1px solid #111111;
          }

          .payments {
            padding: 10px 0;
            border-bottom: 1px dashed #777777;
          }

          .footer {
            text-align: center;
            padding-top: 14px;
            font-size: 10px;
            color: #555555;
            line-height: 16px;
          }

          @media print {
            @page {
              size: 80mm auto;
              margin: 0;
            }

            body {
              width: 80mm;
              padding: 5mm;
            }
          }
        </style>
      </head>

      <body>
        <div class="receipt">

          <div class="center">
            <div class="title">SOLACEHEALTHHUB</div>
            <div class="subtitle">Transaction Receipt</div>
          </div>

          <div class="meta">
            <div class="meta-row">
              <span>Receipt No.</span>
              <strong>#${escapeHtml(transactionId)}</strong>
            </div>

            <div class="meta-row">
              <span>Date</span>
              <span>${escapeHtml(dateString)}</span>
            </div>

            <div class="meta-row">
              <span>Time</span>
              <span>${escapeHtml(timeString)}</span>
            </div>

            <div class="meta-row">
              <span>Cashier</span>
              <strong>${escapeHtml(cashierName || "Cashier")}</strong>
            </div>
          </div>

          <div class="items">
            ${itemRows || "<div>No items</div>"}
          </div>

          <div class="summary">
            <div class="row">
              <span>Subtotal</span>
              <span>${money(subtotal)}</span>
            </div>

            <div class="row">
              <span>Item Discount</span>
              <span>-${money(itemDiscount)}</span>
            </div>

            <div class="row">
              <span>Total Discount</span>
              <span>-${money(totalDiscount)}</span>
            </div>

            <div class="row">
              <span>Tax</span>
              <span>${money(tax)}</span>
            </div>

            <div class="row grand-total">
              <span>TOTAL</span>
              <span>${money(total)}</span>
            </div>
          </div>

          <div class="payments">
            <div style="font-weight: 800; margin-bottom: 4px;">
              PAYMENT
            </div>

            ${paymentRows}

            <div class="row">
              <span>Amount Paid</span>
              <span>${money(paid)}</span>
            </div>

            <div class="row">
              <strong>Change</strong>
              <strong>${money(change)}</strong>
            </div>
          </div>

          <div class="footer">
            Thank you for your purchase.<br />
            Please keep this receipt for your records.
          </div>

        </div>

        <script>
          window.onload = function () {
            setTimeout(function () {
              window.print();
            }, 300);
          };

          window.onafterprint = function () {
            setTimeout(function () {
              window.close();
            }, 300);
          };
        </script>
      </body>
    </html>
  `;

  const printWindow = window.open(
    "",
    "_blank",
    "width=420,height=700"
  );

  if (!printWindow) {
    alert(
      "The receipt window was blocked. Please allow pop-ups for this POS."
    );
    return;
  }

  printWindow.document.open();
  printWindow.document.write(receiptHtml);
  printWindow.document.close();
};

export default printReceipt;







